import { FormEvent, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { mascaraMoedaBR } from '../lib/moeda';
import { dataHora } from '../lib/rotulos';
import { Link } from 'react-router-dom';

interface Material {
  id: number;
  nome: string;
  codigo: string | null;
  categoria: string | null;
  unidade_medida: string | null;
  quantidade_atual: string | number;
  valor_unitario: string | number;
  status: string;
}

interface Movimento {
  id: number;
  material_id: number;
  material_nome: string;
  ordem_servico_id: number | null;
  tipo: string;
  quantidade: string | number;
  valor_unitario: string | number;
  valor_total: string | number;
  observacao: string | null;
  created_at: string;
}

function erro(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

// O Postgres devolve numeric como string "1.000"/"0.100"; formatamos em pt-BR
// para não ler "1.000" como mil. Quantidade: até 3 casas, sem zeros à toa.
const num = (v: unknown): number => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const fmtQtd = (v: unknown): string => num(v).toLocaleString('pt-BR', { maximumFractionDigits: 3 });
const fmtMoeda = (v: unknown): string => num(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Unidades de medida (select fixo, em vez de texto livre).
const UNIDADES: { sigla: string; nome: string }[] = [
  { sigla: 'un', nome: 'Unidade' },
  { sigla: 'cx', nome: 'Caixa' },
  { sigla: 'pç', nome: 'Peça' },
  { sigla: 'par', nome: 'Par' },
  { sigla: 'pct', nome: 'Pacote' },
  { sigla: 'rolo', nome: 'Rolo' },
  { sigla: 'm', nome: 'Metro' },
  { sigla: 'm²', nome: 'Metro quadrado' },
  { sigla: 'L', nome: 'Litro' },
  { sigla: 'mL', nome: 'Mililitro' },
  { sigla: 'kg', nome: 'Quilograma' },
  { sigla: 'g', nome: 'Grama' },
];

// Campo de dinheiro: exibe "R$ 20,00" e guarda o número em reais. Cada dígito
// digitado entra como centavo (digita 2000 -> R$ 20,00), padrão de máscara de moeda.
const valorMoeda = (v: unknown): string => 'R$ ' + fmtMoeda(v);
const parseMoedaInput = (raw: string): number => {
  const digitos = raw.replace(/\D/g, '');
  return digitos ? Number(digitos) / 100 : 0;
};

const POR_PAGINA = 5;

// Altura fixa dos dois cards de tabela: cabe as 5 linhas da pagina no pior caso
// (nome em 2 linhas + codigo). Nao muda com o tamanho do texto nem com a
// quantidade de linhas da pagina - por isso e fixa, e nao min/max.
const ALTURA_TABELA = 448;

// Nome longo para em 2 linhas com reticencias, em vez de esticar a linha. O texto
// inteiro continua acessivel no title (tooltip).
const nomeCurto: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

// Paginador das duas tabelas. Fora do componente para nao ser recriado a cada render.
function Paginacao({ pagina, total, onMudar }: { pagina: number; total: number; onMudar: (p: number) => void }) {
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA));
  // Renderiza mesmo com uma pagina so: o rodape das duas colunas precisa ter a
  // mesma altura para as tabelas alinharem, e a contagem e util de qualquer jeito.
  const de = total ? (pagina - 1) * POR_PAGINA + 1 : 0;
  const ate = Math.min(pagina * POR_PAGINA, total);
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end', marginTop: 8 }}>
      <small style={{ color: 'var(--texto-suave)', marginRight: 'auto' }}>{de}-{ate} de {total}</small>
      <button type="button" className="btn sec sm" disabled={pagina <= 1} onClick={() => onMudar(1)} title="Primeira pagina">&laquo;</button>
      <button type="button" className="btn sec sm" disabled={pagina <= 1} onClick={() => onMudar(pagina - 1)}>&lsaquo;</button>
      <small style={{ color: 'var(--texto-suave)' }}>{pagina} / {paginas}</small>
      <button type="button" className="btn sec sm" disabled={pagina >= paginas} onClick={() => onMudar(pagina + 1)}>&rsaquo;</button>
      <button type="button" className="btn sec sm" disabled={pagina >= paginas} onClick={() => onMudar(paginas)} title="Ultima pagina">&raquo;</button>
    </div>
  );
}

const fatia = <T,>(lista: T[], pagina: number): T[] => lista.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

export default function Estoque() {
  const { isPremium } = useAuth();
  const [materiais, setMateriais] = useState<Material[]>([]);
  const [movimentos, setMovimentos] = useState<Movimento[]>([]);
  const [form, setForm] = useState<Partial<Material>>({ status: 'ativo', unidade_medida: 'un', quantidade_atual: 0, valor_unitario: 0 });
  const [mov, setMov] = useState({ material_id: '', tipo: 'entrada', quantidade: '', valor_unitario: '', observacao: '' });
  const [erroMsg, setErroMsg] = useState('');
  const [msg, setMsg] = useState('');
  const [busca, setBusca] = useState('');
  const [categoria, setCategoria] = useState('');
  const [situacao, setSituacao] = useState('ativo');
  const [paginaMat, setPaginaMat] = useState(1);
  const [buscaMov, setBuscaMov] = useState('');
  const [paginaMov, setPaginaMov] = useState(1);
  // Edicao do valor direto na linha da tabela: id do material em edicao e o
  // texto digitado (mascarado). Nao mexe no formulario ao lado.
  const [valorEditandoId, setValorEditandoId] = useState<number | null>(null);
  const [valorRascunho, setValorRascunho] = useState('');

  async function carregar() {
    setErroMsg('');
    const [m, mv] = await Promise.all([
      api<Material[]>('/estoque/materiais.php'),
      api<Movimento[]>('/estoque/movimentacoes.php'),
    ]);
    setMateriais(m);
    setMovimentos(mv);
  }

  useEffect(() => {
    if (isPremium) carregar().catch((e) => setErroMsg(erro(e)));
  }, [isPremium]);

  async function executar(fn: () => Promise<void>, sucesso: string) {
    setErroMsg('');
    setMsg('');
    try {
      await fn();
      setMsg(sucesso);
      await carregar();
    } catch (e) {
      setErroMsg(erro(e));
    }
  }

  async function salvarMaterial(e: FormEvent) {
    e.preventDefault();
    const body = {
      id: form.id,
      nome: (form.nome || '').trim(),
      codigo: form.codigo || null,
      categoria: form.categoria || null,
      unidade_medida: form.unidade_medida || null,
      quantidade_atual: Math.round(Number(form.quantidade_atual || 0)),
      valor_unitario: Number(form.valor_unitario || 0),
      status: form.status || 'ativo',
    };
    await executar(async () => {
      if (form.id) await api('/estoque/materiais.php', { method: 'PUT', body });
      else await api('/estoque/materiais.php', { method: 'POST', body });
      setForm({ status: 'ativo', unidade_medida: 'un', quantidade_atual: 0, valor_unitario: 0 });
    }, form.id ? 'Material atualizado.' : 'Material criado.');
  }

  // Ao escolher o material, traz o valor cadastrado dele para o campo. Continua
  // editavel: entrada por outro preco (compra, ajuste) so digita por cima.
  function selecionarMaterialMov(materialId: string) {
    const escolhido = materiais.find((m) => String(m.id) === materialId);
    setMov({
      ...mov,
      material_id: materialId,
      valor_unitario: escolhido ? fmtMoeda(escolhido.valor_unitario) : '',
    });
  }

  async function salvarMovimento(e: FormEvent) {
    e.preventDefault();
    const body = {
      material_id: Number(mov.material_id),
      tipo: mov.tipo,
      quantidade: Math.round(Number(mov.quantidade)),
      ...(mov.valor_unitario !== '' ? { valor_unitario: parseMoedaInput(mov.valor_unitario) } : {}),
      observacao: mov.observacao || null,
    };
    await executar(async () => {
      await api('/estoque/movimentacoes.php', { method: 'POST', body });
      setMov({ material_id: '', tipo: 'entrada', quantidade: '', valor_unitario: '', observacao: '' });
    }, 'Movimentação manual registrada.');
  }

  function abrirValor(m: Material) {
    setValorEditandoId(m.id);
    setValorRascunho(fmtMoeda(m.valor_unitario));
  }

  function fecharValor() {
    setValorEditandoId(null);
    setValorRascunho('');
  }

  async function salvarValor(m: Material) {
    // Guarda contra dupla execucao: Enter e Escape fecham a edicao, o input
    // desmonta e o onBlur dispara em seguida com o rascunho JA limpo. Sem isto,
    // esse segundo disparo gravaria R$ 0,00 por cima do valor do material.
    if (!valorRascunho.trim()) {
      fecharValor();
      return;
    }
    const novo = parseMoedaInput(valorRascunho);
    if (novo === num(m.valor_unitario)) {
      fecharValor();
      return;
    }
    await executar(async () => {
      await api('/estoque/materiais.php', { method: 'PUT', body: { id: m.id, valor_unitario: novo } });
      fecharValor();
    }, 'Valor atualizado.');
  }

  // Filtro de nome/codigo, categoria e situacao: com a tabela de pecas do cliente
  // importada a lista passa de 400 itens e fica impraticavel sem busca.
  const categorias = Array.from(new Set(materiais.map((m) => m.categoria).filter((c): c is string => Boolean(c)))).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  const temCategoria = categorias.length > 0;
  const termo = busca.trim().toLowerCase();
  const materiaisFiltrados = materiais.filter((m) => {
    if (situacao && (m.status || 'ativo') !== situacao) return false;
    if (categoria && (m.categoria || '') !== categoria) return false;
    if (!termo) return true;
    return (m.nome || '').toLowerCase().includes(termo) || (m.codigo || '').toLowerCase().includes(termo);
  });
  const termoMov = buscaMov.trim().toLowerCase();
  const movimentosFiltrados = movimentos.filter(
    (m) => !termoMov || (m.material_nome || '').toLowerCase().includes(termoMov),
  );

  // Pagina corrente limitada ao total: se o filtro encurtar a lista, a tabela nao
  // fica em branco numa pagina que deixou de existir.
  const pagMat = Math.min(paginaMat, Math.max(1, Math.ceil(materiaisFiltrados.length / POR_PAGINA)));
  const pagMov = Math.min(paginaMov, Math.max(1, Math.ceil(movimentosFiltrados.length / POR_PAGINA)));

  // Material nunca e excluido, so DESATIVADO (o DELETE do endpoint tambem
  // desativa). O PUT esta aqui porque esta tela tambem reativa.
  async function alternarSituacao(m: Material) {
    const inativo = (m.status || 'ativo') === 'inativo';
    await executar(
      () => api('/estoque/materiais.php', { method: 'PUT', body: { id: m.id, status: inativo ? 'ativo' : 'inativo' } }),
      inativo ? 'Material reativado.' : 'Material desativado.',
    );
  }

  if (!isPremium) {
    return (
      <div className="card">
        <h2>Estoque</h2>
        <p style={{ color: 'var(--texto-suave)' }}>Controle de materiais e movimentações faz parte do plano Premium.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="titulo-pg">
        <h1>Estoque</h1>
        <button className="btn sec sm" onClick={() => carregar().catch((e) => setErroMsg(erro(e)))}>Atualizar</button>
      </div>
      <p style={{ color: 'var(--texto-suave)', margin: '0 0 12px', fontSize: 14 }}>
        Cadastre materiais, registre compras, ajustes e saídas avulsas. A baixa por OS deve ser feita dentro da própria ordem de serviço.
      </p>
      {erroMsg && <div className="msg-erro">{erroMsg}</div>}
      {msg && <div className="msg-ok">{msg}</div>}

      <div className="grid cols2">
        <form className="card" onSubmit={salvarMaterial} style={{ display: 'flex', flexDirection: 'column' }}>
          <h3>{form.id ? 'Editar material' : 'Novo material'}</h3>
          <p style={{ color: 'var(--texto-suave)', margin: '0 0 10px', fontSize: 13 }}>
            Cadastre cada item que você controla no estoque. Depois registre entradas, ajustes ou saídas manuais no painel ao lado.
          </p>
          <label>Nome do material *</label>
          <input className="input" value={form.nome || ''} onChange={(e) => setForm({ ...form, nome: e.target.value })} required placeholder="Ex.: Lâmpada LED 9W" />
          <div className="grid cols2">
            <div><label>Código</label><input className="input" value={form.codigo || ''} onChange={(e) => setForm({ ...form, codigo: e.target.value })} placeholder="Opcional" /></div>
            <div>
              <label>Categoria</label>
              <input className="input" list="lista-categorias-material" value={form.categoria || ''} onChange={(e) => setForm({ ...form, categoria: e.target.value })} placeholder="Opcional (ex.: Capacitores)" />
              <datalist id="lista-categorias-material">
                {categorias.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <label>Unidade de medida</label>
              <select value={form.unidade_medida || 'un'} onChange={(e) => setForm({ ...form, unidade_medida: e.target.value })}>
                {UNIDADES.map((u) => <option key={u.sigla} value={u.sigla}>{u.nome} ({u.sigla})</option>)}
              </select>
            </div>
            <div><label>{form.id ? 'Saldo atual (use Entrada/Saída para alterar)' : 'Quantidade inicial em estoque'}</label><input className="input" type="number" step="1" min="0" inputMode="numeric" value={form.quantidade_atual ?? ''} onChange={(e) => setForm({ ...form, quantidade_atual: e.target.value.replace(/\D/g, '') })} disabled={Boolean(form.id)} /></div>
            <div><label>Valor unitário</label><input className="input" inputMode="numeric" value={valorMoeda(form.valor_unitario)} onChange={(e) => setForm({ ...form, valor_unitario: parseMoedaInput(e.target.value) })} /></div>
          </div>
          <div className="form-actions" style={{ marginTop: 'auto' }}>
            {form.id && <button type="button" className="btn sec" onClick={() => setForm({ status: 'ativo', unidade_medida: 'un', quantidade_atual: 0, valor_unitario: 0 })}>Cancelar</button>}
            <button className="btn">{form.id ? 'Salvar material' : 'Adicionar material'}</button>
          </div>
        </form>

        <form className="card" onSubmit={salvarMovimento} style={{ display: 'flex', flexDirection: 'column' }}>
          <h3>Movimentação manual</h3>
          <p style={{ color: 'var(--texto-suave)', margin: '0 0 10px', fontSize: 13 }}>
            Use para compra, reposição, ajuste de inventário ou saída avulsa. Material usado em OS é lançado na tela da OS.
          </p>
          <label>Material *</label>
          <select value={mov.material_id} onChange={(e) => selecionarMaterialMov(e.target.value)} required>
            <option value="">Selecione</option>
            {materiais.map((m) => <option key={m.id} value={m.id}>{m.nome} ({fmtQtd(m.quantidade_atual)}){(m.status || 'ativo') === 'inativo' ? ' - desativado' : ''}</option>)}
          </select>
          <div className="grid cols2">
            <div>
              <label>Tipo</label>
              <select value={mov.tipo} onChange={(e) => setMov({ ...mov, tipo: e.target.value })}>
                <option value="entrada">Entrada</option><option value="saida">Saída</option>
              </select>
            </div>
            <div><label>Quantidade *</label><input className="input" type="number" step="1" min="0" inputMode="numeric" value={mov.quantidade} onChange={(e) => setMov({ ...mov, quantidade: e.target.value.replace(/\D/g, '') })} required /></div>
            <div><label>Valor unitário</label><input className="input" inputMode="numeric" value={mov.valor_unitario} onChange={(e) => setMov({ ...mov, valor_unitario: mascaraMoedaBR(e.target.value) })} placeholder="usa o valor cadastrado se vazio" /></div>
          </div>
          <label>Motivo / observação</label>
          <input className="input" value={mov.observacao} onChange={(e) => setMov({ ...mov, observacao: e.target.value })} placeholder="Ex.: compra, ajuste de inventário, perda, devolução..." />
          <div className="form-actions" style={{ marginTop: 'auto' }}><button className="btn">Registrar movimentação manual</button></div>
        </form>
      </div>

      <div className="grid cols2" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
            <input
              className="input"
              style={{ flex: '1 1 200px', margin: 0 }}
              value={busca}
              onChange={(e) => { setBusca(e.target.value); setPaginaMat(1); }}
              placeholder="Buscar material ou código"
            />
            {temCategoria && (
              <select style={{ flex: '0 1 190px', margin: 0 }} value={categoria} onChange={(e) => { setCategoria(e.target.value); setPaginaMat(1); }}>
                <option value="">Todas as categorias</option>
                {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            <select style={{ flex: '0 1 145px', margin: 0 }} value={situacao} onChange={(e) => { setSituacao(e.target.value); setPaginaMat(1); }}>
              <option value="ativo">Ativos</option>
              <option value="inativo">Desativados</option>
              <option value="">Todos</option>
            </select>
            <small style={{ color: 'var(--texto-suave)' }}>{materiaisFiltrados.length} de {materiais.length}</small>
          </div>
          <div className="tabela-wrap" style={{ height: ALTURA_TABELA, overflowY: 'auto', marginTop: 'auto' }}>
          <table>
            <thead><tr><th>Material</th>{temCategoria && <th>Categoria</th>}<th>Qtd</th><th>Valor</th><th></th></tr></thead>
            <tbody>
              {fatia(materiaisFiltrados, pagMat).map((m) => (
                <tr key={m.id} style={{ opacity: (m.status || 'ativo') === 'inativo' ? 0.6 : 1 }}>
                  <td>
                    <strong style={nomeCurto} title={m.nome}>{m.nome}</strong>
                    {(m.status || 'ativo') === 'inativo' && (
                      <span className="badge" style={{ background: '#fee2e2', color: '#b91c1c' }}>desativado</span>
                    )}
                    <small style={{ display: 'block' }}>{m.codigo || '-'}</small>
                  </td>
                  {temCategoria && <td>{m.categoria || '-'}</td>}
                  <td><strong style={{ color: num(m.quantidade_atual) <= 0 ? 'var(--erro)' : 'inherit' }}>{fmtQtd(m.quantidade_atual)}</strong> {m.unidade_medida || ''}</td>
                  <td>
                    {valorEditandoId === m.id ? (
                      <input
                        className="input"
                        inputMode="numeric"
                        autoFocus
                        style={{ margin: 0, minWidth: 110 }}
                        value={valorRascunho}
                        onChange={(e) => setValorRascunho(mascaraMoedaBR(e.target.value))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); salvarValor(m); }
                          if (e.key === 'Escape') fecharValor();
                        }}
                        onBlur={() => salvarValor(m)}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => abrirValor(m)}
                        title="Clique para editar o valor"
                        style={{
                          background: 'none',
                          border: 0,
                          padding: 0,
                          font: 'inherit',
                          color: 'inherit',
                          cursor: 'pointer',
                          borderBottom: '1px dashed var(--cinza-borda)',
                        }}
                      >
                        R$ {fmtMoeda(m.valor_unitario)}
                      </button>
                    )}
                  </td>
                  <td className="acoes">
                    <button className="btn sec sm" onClick={() => setForm({ ...m, quantidade_atual: num(m.quantidade_atual), valor_unitario: num(m.valor_unitario) })}>Editar</button>
                    <button
                      className={(m.status || 'ativo') === 'inativo' ? 'btn sec sm' : 'btn danger sm'}
                      onClick={() => alternarSituacao(m)}
                    >
                      {(m.status || 'ativo') === 'inativo' ? 'Reativar' : 'Desativar'}
                    </button>
                  </td>
                </tr>
              ))}
              {!materiaisFiltrados.length && (
                <tr><td colSpan={temCategoria ? 5 : 4} className="vazio">{materiais.length ? 'Nenhum material para este filtro.' : 'Nenhum material cadastrado.'}</td></tr>
              )}
            </tbody>
          </table>
          </div>
          <Paginacao pagina={pagMat} total={materiaisFiltrados.length} onMudar={setPaginaMat} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
            <input
              className="input"
              style={{ flex: '1 1 200px', margin: 0 }}
              value={buscaMov}
              onChange={(e) => { setBuscaMov(e.target.value); setPaginaMov(1); }}
              placeholder="Buscar pelo nome do material"
            />
            <small style={{ color: 'var(--texto-suave)' }}>{movimentosFiltrados.length} de {movimentos.length}</small>
          </div>
          <div className="tabela-wrap" style={{ height: ALTURA_TABELA, overflowY: 'auto', marginTop: 'auto' }}>
          <table>
            <thead><tr><th>Quando</th><th>Material</th><th>Movimento</th><th>Total</th></tr></thead>
            <tbody>
              {fatia(movimentosFiltrados, pagMov).map((m) => (
                <tr key={m.id}>
                  <td>{dataHora(m.created_at)}</td>
                  <td>
                    <span style={nomeCurto} title={m.material_nome}>{m.material_nome}</span>
                    {m.ordem_servico_id
                      ? <small style={{ display: 'block' }}><Link to={'/app/os/' + m.ordem_servico_id}>OS #{m.ordem_servico_id}</Link></small>
                      : null}
                  </td>
                  <td><span className="badge" style={{ background: m.tipo === 'entrada' ? '#dcfce7' : '#fee2e2', color: m.tipo === 'entrada' ? '#15803d' : '#b91c1c' }}>{m.tipo === 'entrada' ? '↑ entrada' : '↓ saída'}</span> {fmtQtd(m.quantidade)}</td>
                  <td>R$ {fmtMoeda(m.valor_total)}</td>
                </tr>
              ))}
              {!movimentosFiltrados.length && (
                <tr><td colSpan={4} className="vazio">{movimentos.length ? 'Nenhuma movimentação para este filtro.' : 'Nenhuma movimentação registrada.'}</td></tr>
              )}
            </tbody>
          </table>
          </div>
          <Paginacao pagina={pagMov} total={movimentosFiltrados.length} onMudar={setPaginaMov} />
        </div>
      </div>
    </div>
  );
}
